import React from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { useRoleValidation } from '../hooks/useRoleValidation';

// Layout Components
import Layout from '../components/Layout/Layout';

// Landing Page
import LandingPage from '../pages/LandingPage';

// Authentication Pages
import UnifiedLoginPage from '../pages/auth/UnifiedLoginPage';
import PasswordResetPage from '../pages/auth/PasswordResetPage';

// Dashboard Pages
import AdminDashboard from '../pages/dashboard/AdminDashboard';
import HostDashboard from '../pages/dashboard/HostDashboard';
import GuestDashboard from '../pages/dashboard/GuestDashboard';

// Profile Pages
import ProfilePage from '../pages/profile/ProfilePage';

// User Management Pages
import UserManagementPage from '../pages/users/UserManagementPage';

// Billing Pages
import AdminPricingPage from '../pages/billing/AdminPricingPage';
import AdminBalancePage from '../pages/billing/AdminBalancePage';
import HostBalancePage from '../pages/billing/HostBalancePage';

// Host Guests Pages
import HostGuestsPage from '../pages/guests/HostGuestsPage';

// Event Pages
import EventsPage from '../pages/events/EventsPageImproved';
import CreateEventPage2 from '../pages/events/CreateEventPage2';

// Invitation Pages
import InvitationPage from '../pages/invitation/InvitationPage';


// Series Pages
import SeriesListPage from '../pages/series/SeriesListPage';

import EventViewPage from '../pages/events/EventViewPage';


// Category Pages
import CategoriesPage from '../pages/categories/CategoriesPageSimple';

// Analytics Pages
import AdminAnalyticsPage from '../pages/analytics/AdminAnalyticsPage';
import HostAnalyticsPage from '../pages/analytics/HostAnalyticsPage';
import GuestAnalyticsPage from '../pages/analytics/GuestAnalyticsPage';

// Usher Pages
import UsherDashboardPage from '../pages/usher/UsherDashboardPage';
import UsherEventGuestsPage from '../pages/usher/UsherEventGuestsPage';

// UI Showcase (temporary)
import InputShowcase from '../components/UI/InputShowcase';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('admin' | 'host' | 'guest' | 'usher')[];
  requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = ['admin', 'host', 'guest', 'usher'],
  requireAuth = true 
}) => {
  const { isAuthenticated, user, sessionChecked } = useAppSelector((state) => state.auth);
  
  // Validate role from backend on-demand (only when accessing this route)
  // If allowedRoles has only one role, validate against that specific role
  const requiredRole = allowedRoles.length === 1 ? allowedRoles[0] : undefined;
  const { isValidating, isValid, userRole, error } = useRoleValidation({
    requiredRole,
    cacheDuration: 30000, // Cache for 30 seconds
    skipIfCached: true,
  });

  // Wait for session check to complete before making auth decisions
  if (!sessionChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Checking session...</p>
        </div>
      </div>
    );
  }

  // Only redirect if session check is complete and user is not authenticated
  if (requireAuth && sessionChecked && !isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show loading state during role validation
  if (requireAuth && isAuthenticated && isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Handle validation errors (network issues, etc.)
  // If validation failed but we have a user, proceed with stored role (backend will block if wrong)
  if (requireAuth && isAuthenticated && error && !user) {
    return <Navigate to="/" replace />;
  }

  // Check if user's role (from backend validation) is allowed
  // Use active_role from token if available, otherwise fall back to user.role
  const currentRole = (userRole || user?.active_role || user?.role) as 'admin' | 'host' | 'guest' | 'usher' | undefined;
  
  if (requireAuth && currentRole && !allowedRoles.includes(currentRole)) {
    // Redirect to appropriate dashboard based on active role
    switch (currentRole) {
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'host':
        return <Navigate to="/host/dashboard" replace />;
      case 'guest':
        return <Navigate to="/guest/dashboard" replace />;
      case 'usher':
        return <Navigate to="/usher/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  // If validation explicitly failed (not just null/undefined), deny access
  if (requireAuth && isValid === false && requiredRole) {
    // Role doesn't match required role - redirect to appropriate dashboard based on active role
    if (currentRole) {
      switch (currentRole) {
        case 'admin':
          return <Navigate to="/admin/dashboard" replace />;
        case 'host':
          return <Navigate to="/host/dashboard" replace />;
        case 'guest':
          return <Navigate to="/guest/dashboard" replace />;
        case 'usher':
          return <Navigate to="/usher/dashboard" replace />;
        default:
          return <Navigate to="/" replace />;
      }
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Auth Guard - redirect authenticated users away from auth pages
// Exception: Allow login page if invitation_token is present (for phone verification redirect)
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation_token');

  if (isAuthenticated && user) {
    // If invitation_token is present, allow login page to handle redirect after phone verification
    if (invitationToken) {
      return <>{children}</>;
    }
    
    // Use active_role from token if available, otherwise use primary role
    const activeRole = user.active_role || user.role;
    switch (activeRole) {
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'host':
        return <Navigate to="/host/dashboard" replace />;
      case 'guest':
        return <Navigate to="/guest/dashboard" replace />;
      case 'usher':
        return <Navigate to="/usher/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

const AppRouter: React.FC = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  return (
    <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          isAuthenticated && user ? (
            <Navigate to={`/${user.active_role || user.role}/dashboard`} replace />
          ) : (
            <LandingPage />
          )
        } />
        
        {/* Unified Login Route */}
        <Route path="/login" element={
          <AuthGuard>
            <UnifiedLoginPage />
          </AuthGuard>
        } />
        
        <Route path="/auth/password-reset" element={
          <AuthGuard>
            <PasswordResetPage />
          </AuthGuard>
        } />

        {/* Admin Routes */}
        <Route path="/admin/*" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout>
              <Routes>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="events/create" element={<CreateEventPage2 />} />
                <Route path="series" element={<SeriesListPage />} />
                <Route path="events/:slug" element={<EventViewPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="users" element={<UserManagementPage />} />
                <Route path="billing/pricing" element={<AdminPricingPage />} />
                <Route path="billing/balances" element={<AdminBalancePage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />

        {/* Host Routes */}
        <Route path="/host/*" element={
          <ProtectedRoute allowedRoles={['host']}>
            <Layout>
              <Routes>
                <Route path="dashboard" element={<HostDashboard />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="events/create" element={<CreateEventPage2 />} />
                <Route path="series" element={<SeriesListPage />} />
                <Route path="events/:slug" element={<EventViewPage />} />
                <Route path="guests" element={<HostGuestsPage />} />
                <Route path="billing" element={<HostBalancePage />} />
                <Route path="analytics" element={<HostAnalyticsPage />} />
                <Route path="*" element={<Navigate to="/host/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />

        {/* Guest Routes */}
        <Route path="/guest/*" element={
          <ProtectedRoute allowedRoles={['guest']}>
            <Layout>
              <Routes>
                <Route path="dashboard" element={<GuestDashboard />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="analytics" element={<GuestAnalyticsPage />} />
                <Route path="*" element={<Navigate to="/guest/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />

        {/* Usher Routes */}
        <Route path="/usher/*" element={
          <ProtectedRoute allowedRoles={['usher']}>
            <Routes>
              <Route path="dashboard" element={<UsherDashboardPage />} />
              <Route path="events/:eventId/guests" element={<UsherEventGuestsPage />} />
              <Route path="*" element={<Navigate to="/usher/dashboard" replace />} />
            </Routes>
          </ProtectedRoute>
        } />

        {/* Invitation Routes (Public) */}
        <Route path="/invitation/:token" element={<InvitationPage />} />

        {/* UI Showcase (temporary demo) */}
        <Route path="/showcase" element={<InputShowcase />} />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRouter;
